import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit2, Trash2, Clock, Calendar, Power, Settings, Zap } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CallbackMaster = () => {
  const { toast } = useToast();
  const [slots, setSlots] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [editingSlot, setEditingSlot] = useState(null);
  
  const [formData, setFormData] = useState({
    slot_label: '',
    start_time: '',
    end_time: '',
    available_days: [],
    max_capacity: '',
    is_active: true,
    display_order: 0
  });

  const [bulkConfig, setBulkConfig] = useState({
    start_time: '09:00',
    end_time: '18:00',
    slot_gap_minutes: 120,
    working_days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
    max_capacity: '',
    overwrite_existing: false
  });

  const weekDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  useEffect(() => {
    fetchSlots();
  }, []);

  const fetchSlots = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        console.error('No authentication token found');
        return;
      }
      const response = await axios.get(`${API}/callback-slots`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSlots(response.data);
    } catch (error) {
      console.error('Failed to fetch slots:', error);
      if (error.response?.status === 401) {
        toast({
          title: "Authentication Error",
          description: "Please log out and log in again",
          variant: "destructive"
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to fetch callback slots",
          variant: "destructive"
        });
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        toast({
          title: "Authentication Error",
          description: "Please log in again",
          variant: "destructive"
        });
        return;
      }

      const submitData = {
        ...formData,
        max_capacity: formData.max_capacity ? parseInt(formData.max_capacity) : null
      };

      if (editingSlot) {
        await axios.put(`${API}/callback-slots/${editingSlot.id}`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({
          title: "Success",
          description: "Slot updated successfully"
        });
      } else {
        await axios.post(`${API}/callback-slots`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({
          title: "Success",
          description: "Slot created successfully"
        });
      }

      fetchSlots();
      resetForm();
      setShowModal(false);
    } catch (error) {
      console.error('Failed to save slot:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to save slot",
        variant: "destructive"
      });
    }
  };

  const handleBulkGenerate = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        toast({
          title: "Authentication Error",
          description: "Please log in again",
          variant: "destructive"
        });
        return;
      }

      const submitData = {
        ...bulkConfig,
        max_capacity: bulkConfig.max_capacity ? parseInt(bulkConfig.max_capacity) : null
      };

      const response = await axios.post(`${API}/callback-slots/bulk-generate`, submitData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast({
        title: "Success",
        description: response.data.message
      });
      
      fetchSlots();
      setShowBulkModal(false);
    } catch (error) {
      console.error('Failed to generate slots:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to generate slots",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async (slotId) => {
    const confirmed = await window.confirm('Are you sure you want to delete this slot?');
    if (!confirmed) return;

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/callback-slots/${slotId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: "Success",
        description: "Slot deleted successfully"
      });
      
      fetchSlots();
    } catch (error) {
      console.error('Failed to delete slot:', error);
      toast({
        title: "Error",
        description: "Failed to delete slot",
        variant: "destructive"
      });
    }
  };

  const handleToggleActive = async (slot) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API}/callback-slots/${slot.id}`, 
        { is_active: !slot.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchSlots();
      toast({
        title: "Success",
        description: `Slot ${!slot.is_active ? 'activated' : 'deactivated'} successfully`
      });
    } catch (error) {
      console.error('Failed to toggle slot status:', error);
      toast({
        title: "Error",
        description: "Failed to update slot status",
        variant: "destructive"
      });
    }
  };

  const handleEdit = (slot) => {
    setEditingSlot(slot);
    setFormData({
      slot_label: slot.slot_label,
      start_time: slot.start_time,
      end_time: slot.end_time,
      available_days: slot.available_days,
      max_capacity: slot.max_capacity || '',
      is_active: slot.is_active,
      display_order: slot.display_order
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingSlot(null);
    setFormData({
      slot_label: '',
      start_time: '',
      end_time: '',
      available_days: [],
      max_capacity: '',
      is_active: true,
      display_order: 0
    });
  };

  const handleDayToggle = (day) => {
    setFormData(prev => ({
      ...prev,
      available_days: prev.available_days.includes(day)
        ? prev.available_days.filter(d => d !== day)
        : [...prev.available_days, day]
    }));
  };

  const handleBulkDayToggle = (day) => {
    setBulkConfig(prev => ({
      ...prev,
      working_days: prev.working_days.includes(day)
        ? prev.working_days.filter(d => d !== day)
        : [...prev.working_days, day]
    }));
  };

  return (
    <div className="admin-content">
      <div className="page-header">
        <div>
          <h1 className="heading-1">Call Back Master</h1>
          <p className="body-medium">Manage callback time slots for consultations</p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button onClick={() => setShowBulkModal(true)} className="btn-secondary">
            <Zap size={18} />
            <span>Auto Generate Slots</span>
          </button>
          <button onClick={() => { resetForm(); setShowModal(true); }} className="btn-primary">
            <Plus size={18} />
            <span>Add Slot</span>
          </button>
        </div>
      </div>

      <div className="content-card">
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Time Slot</th>
                <th>Start Time</th>
                <th>End Time</th>
                <th>Available Days</th>
                <th>Capacity</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {slots.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: 'center', padding: '40px' }}>
                    <Clock size={48} style={{ margin: '0 auto 16px', opacity: 0.3 }} />
                    <p className="body-large">No callback slots found</p>
                    <p className="body-small">Click "Add Slot" or "Auto Generate Slots" to create time slots</p>
                  </td>
                </tr>
              ) : (
                slots.map((slot) => (
                  <tr key={slot.id}>
                    <td className="body-medium">{slot.slot_label}</td>
                    <td className="body-small">{slot.start_time}</td>
                    <td className="body-small">{slot.end_time}</td>
                    <td className="body-small">{slot.available_days.join(', ')}</td>
                    <td className="body-small">{slot.max_capacity || 'Unlimited'}</td>
                    <td>
                      <span className={`status-badge ${slot.is_active ? 'active' : 'inactive'}`}>
                        {slot.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className="table-actions">
                        <button onClick={() => handleToggleActive(slot)} className="icon-btn" title={slot.is_active ? 'Deactivate' : 'Activate'}>
                          <Power size={18} color={slot.is_active ? '#22c55e' : '#94a3b8'} />
                        </button>
                        <button onClick={() => handleEdit(slot)} className="icon-btn" title="Edit">
                          <Edit2 size={18} />
                        </button>
                        <button onClick={() => handleDelete(slot.id)} className="icon-btn danger" title="Delete">
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Slot Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="heading-2">{editingSlot ? 'Edit Slot' : 'Add New Slot'}</h2>
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-group">
                <label className="form-label">Slot Label (Optional - auto-generated if empty)</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.slot_label}
                  onChange={(e) => setFormData({...formData, slot_label: e.target.value})}
                  placeholder="e.g., 10:00 AM - 12:00 PM"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Start Time *</label>
                  <input
                    type="time"
                    className="form-input"
                    value={formData.start_time}
                    onChange={(e) => setFormData({...formData, start_time: e.target.value})}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">End Time *</label>
                  <input
                    type="time"
                    className="form-input"
                    value={formData.end_time}
                    onChange={(e) => setFormData({...formData, end_time: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Available Days *</label>
                <div className="days-selector">
                  {weekDays.map(day => (
                    <button
                      key={day}
                      type="button"
                      onClick={() => handleDayToggle(day)}
                      className={`day-btn ${formData.available_days.includes(day) ? 'selected' : ''}`}
                    >
                      {day.substring(0, 3)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Max Capacity (Optional)</label>
                <input
                  type="number"
                  className="form-input"
                  value={formData.max_capacity}
                  onChange={(e) => setFormData({...formData, max_capacity: e.target.value})}
                  placeholder="Leave empty for unlimited"
                  min="1"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Display Order</label>
                <input
                  type="number"
                  className="form-input"
                  value={formData.display_order}
                  onChange={(e) => setFormData({...formData, display_order: parseInt(e.target.value)})}
                  min="0"
                />
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                  />
                  <span>Active</span>
                </label>
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingSlot ? 'Update Slot' : 'Create Slot'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Generate Modal */}
      {showBulkModal && (
        <div className="modal-overlay" onClick={() => setShowBulkModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="heading-2">Auto Generate Slots</h2>
            <form onSubmit={handleBulkGenerate} className="modal-form">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Business Start Time *</label>
                  <input
                    type="time"
                    className="form-input"
                    value={bulkConfig.start_time}
                    onChange={(e) => setBulkConfig({...bulkConfig, start_time: e.target.value})}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Business End Time *</label>
                  <input
                    type="time"
                    className="form-input"
                    value={bulkConfig.end_time}
                    onChange={(e) => setBulkConfig({...bulkConfig, end_time: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Slot Gap (in minutes) *</label>
                <input
                  type="number"
                  className="form-input"
                  value={bulkConfig.slot_gap_minutes}
                  onChange={(e) => setBulkConfig({...bulkConfig, slot_gap_minutes: parseInt(e.target.value)})}
                  placeholder="e.g., 120 for 2 hours"
                  min="15"
                  step="15"
                  required
                />
                <p className="body-small" style={{ marginTop: '4px', color: '#64748b' }}>
                  Example: 60 = 1 hour, 120 = 2 hours, 30 = 30 minutes
                </p>
              </div>

              <div className="form-group">
                <label className="form-label">Working Days *</label>
                <div className="days-selector">
                  {weekDays.map(day => (
                    <button
                      key={day}
                      type="button"
                      onClick={() => handleBulkDayToggle(day)}
                      className={`day-btn ${bulkConfig.working_days.includes(day) ? 'selected' : ''}`}
                    >
                      {day.substring(0, 3)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Max Capacity Per Slot (Optional)</label>
                <input
                  type="number"
                  className="form-input"
                  value={bulkConfig.max_capacity}
                  onChange={(e) => setBulkConfig({...bulkConfig, max_capacity: e.target.value})}
                  placeholder="Leave empty for unlimited"
                  min="1"
                />
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={bulkConfig.overwrite_existing}
                    onChange={(e) => setBulkConfig({...bulkConfig, overwrite_existing: e.target.checked})}
                  />
                  <span>Overwrite existing slots (Warning: This will delete all current slots)</span>
                </label>
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => setShowBulkModal(false)} className="btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Generate Slots
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CallbackMaster;
